import React, { useState, useEffect, useRef } from 'react';
import { X, ArrowRight, ArrowLeft, CheckCircle } from 'lucide-react';

interface TourStep {
  id: string;
  target: string;
  title: string;
  content: string;
  position: 'top' | 'bottom' | 'left' | 'right' | 'center';
  action?: () => void;
  highlight?: boolean;
}

interface GuidedTourProps {
  isActive: boolean;
  tourType: 'homepage' | 'simulator-investment' | 'simulator-products' | 'simulator-strategies' | 'simulator-needs';
  onComplete: () => void;
  onSkip: () => void;
}

const tourSteps: Record<string, TourStep[]> = {
  homepage: [
    {
      id: 'welcome',
      target: 'hero-title',
      title: '👋 Bienvenue sur SOeasi !',
      content: 'Découvrez notre plateforme d\'investissement 100% digitale et conforme à vos valeurs. Laissez-nous vous faire visiter !',
      position: 'center',
      highlight: true
    },
    {
      id: 'simulators-intro',
      target: 'simulators-section',
      title: 'Nos 4 Simulateurs',
      content: 'Nous avons créé 4 outils puissants pour analyser et optimiser vos investissements selon vos besoins.',
      position: 'top',
      highlight: true
    },
    {
      id: 'investment-sim',
      target: 'sim-investment',
      title: '📈 Simulateur d\'Investissement',
      content: 'Parfait pour débuter ! Visualisez l\'évolution de votre épargne avec un rendement de 4% et des courbes dynamiques.',
      position: 'bottom'
    },
    {
      id: 'products-sim',
      target: 'sim-products',
      title: '🏢 Simulateur de Produits',
      content: 'Comparez nos 3 produits phares : SCPI, GFI et Sukuk. Chacun a ses avantages selon votre profil.',
      position: 'bottom'
    },
    {
      id: 'strategies-sim',
      target: 'sim-strategies',
      title: '🏆 Simulateur de Stratégies',
      content: 'Choisissez entre 3 stratégies gamifiées : Protecteur, Équilibré ou Dynamique selon votre appétit pour le risque.',
      position: 'bottom'
    },
    {
      id: 'needs-sim',
      target: 'sim-needs',
      title: '🤖 Analyse Personnalisée IA',
      content: 'Notre outil le plus sophistiqué ! Il analyse vos besoins et recommande une stratégie 100% sur mesure.',
      position: 'bottom'
    },
    {
      id: 'chat-assistant',
      target: 'chat-fab',
      title: '💬 Assistant IA Toujours Disponible',
      content: 'Besoin d\'aide à tout moment ? Notre Assistant IA est là pour répondre à toutes vos questions !',
      position: 'left'
    },
    {
      id: 'complete',
      target: 'hero-title',
      title: '✨ Prêt à Commencer !',
      content: 'Vous connaissez maintenant tous nos outils. Choisissez celui qui vous intéresse le plus et commencez votre analyse !',
      position: 'center',
      highlight: true
    }
  ],
  'simulator-investment': [
    {
      id: 'welcome-sim',
      target: 'sim-controls',
      title: '🎮 Contrôles Interactifs',
      content: 'Ajustez votre capital initial, vos versements mensuels et la durée. Tout se met à jour en temps réel !',
      position: 'right'
    },
    {
      id: 'chart-explanation',
      target: 'sim-chart',
      title: '📊 Graphique Dynamique',
      content: 'Visualisez l\'évolution de votre investissement. La courbe bleue montre votre portefeuille, la orange vos versements.',
      position: 'top'
    },
    {
      id: 'kpi-cards',
      target: 'sim-kpis',
      title: '📈 Indicateurs Clés',
      content: 'Ces cartes résument vos performances : capital final, gains, rendement annuel et total investi.',
      position: 'bottom'
    }
  ]
};

const GuidedTour: React.FC<GuidedTourProps> = ({ isActive, tourType, onComplete, onSkip }) => {
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [highlightedElement, setHighlightedElement] = useState<HTMLElement | null>(null);
  const [tooltipPosition, setTooltipPosition] = useState({ 
    top: window.innerHeight / 2, 
    left: window.innerWidth / 2 
  });
  const tooltipRef = useRef<HTMLDivElement>(null);
  
  const steps = tourSteps[tourType] || [];
  const currentStep = steps[currentStepIndex];
  const isLastStep = currentStepIndex === steps.length - 1;
  const isFirstStep = currentStepIndex === 0;

  // Réinitialiser l'index quand le tour devient actif
  useEffect(() => {
    if (isActive) {
      setCurrentStepIndex(0);
    }
  }, [isActive, tourType]);

  useEffect(() => {
    if (!isActive || !currentStep) return;

    // Petite attente pour s'assurer que le DOM est rendu
    const timer = setTimeout(() => {
      const targetElement = document.getElementById(currentStep.target) || 
                           document.querySelector(`[data-tour="${currentStep.target}"]`) ||
                           document.querySelector(`.${currentStep.target}`);
      
      console.log('Looking for element:', currentStep.target, 'Found:', targetElement);
      
      if (targetElement) {
        setHighlightedElement(targetElement);
        
        // Scroll vers l'élément d'abord avec plus de contrôle
        const rect = targetElement.getBoundingClientRect();
        const isVisible = (
          rect.top >= 0 &&
          rect.left >= 0 &&
          rect.bottom <= window.innerHeight &&
          rect.right <= window.innerWidth
        );
        
        if (!isVisible) {
          targetElement.scrollIntoView({ 
            behavior: 'smooth', 
            block: 'center',
            inline: 'nearest'
          });
        }
        
        // Attendre un peu pour le scroll puis calculer la position
        setTimeout(() => {
          const rect = targetElement.getBoundingClientRect();
          const windowHeight = window.innerHeight;
          const windowWidth = window.innerWidth;
          
          let top = 0;
          let left = 0;
          
          if (currentStep.position === 'center') {
            top = windowHeight / 2;
            left = windowWidth / 2;
          } else {
            switch (currentStep.position) {
              case 'top':
                top = Math.max(80, rect.top - 80);
                left = Math.min(windowWidth - 200, Math.max(200, rect.left + rect.width / 2));
                break;
              case 'bottom':
                top = Math.min(windowHeight - 200, rect.bottom + 20);
                left = Math.min(windowWidth - 200, Math.max(200, rect.left + rect.width / 2));
                break;
              case 'left':
                top = Math.min(windowHeight - 150, Math.max(150, rect.top + rect.height / 2));
                left = Math.max(20, rect.left - 370);
                break;
              case 'right':
                top = Math.min(windowHeight - 150, Math.max(150, rect.top + rect.height / 2));
                left = Math.min(windowWidth - 370, rect.right + 20);
                break;
              default:
                top = Math.min(windowHeight - 200, rect.bottom + 20);
                left = Math.min(windowWidth - 200, Math.max(200, rect.left + rect.width / 2));
            }
          }
          
          // Vérifications des limites et ajustements finaux
          top = Math.max(20, Math.min(windowHeight - 300, top));
          left = Math.max(20, Math.min(windowWidth - 400, left));
          
          console.log('Tooltip positioned at:', { top, left, windowHeight, windowWidth });
          setTooltipPosition({ top, left });
        }, 600);
        
      } else {
        console.warn(`Element with target "${currentStep.target}" not found`);
        setHighlightedElement(null);
        
        // Si l'élément n'est pas trouvé, on positionne le tooltip au centre
        const windowHeight = window.innerHeight;
        const windowWidth = window.innerWidth;
        setTooltipPosition({ 
          top: windowHeight / 2, 
          left: windowWidth / 2 
        });
      }
    }, 200);

    return () => {
      clearTimeout(timer);
      setHighlightedElement(null);
    };
  }, [currentStep, isActive]);

  const nextStep = () => {
    if (currentStep.action) {
      currentStep.action();
    }
    
    if (isLastStep) {
      onComplete();
    } else {
      setCurrentStepIndex(prev => prev + 1);
    }
  };

  const prevStep = () => {
    if (!isFirstStep) {
      setCurrentStepIndex(prev => prev - 1);
    }
  };

  const skipTour = () => {
    onSkip();
  };

  if (!isActive || !currentStep) return null;

  return (
    <>
      {/* Overlay sombre */}
      <div className="fixed inset-0 bg-black bg-opacity-40 z-[9998] pointer-events-none">
        {/* Spotlight sur l'élément highlighted */}
        {highlightedElement && currentStep.highlight && (
          <div
            className="absolute bg-transparent border-2 border-blue-400 rounded-lg pointer-events-none shadow-[0_0_0_9999px_rgba(0,0,0,0.4)]"
            style={{
              top: highlightedElement.offsetTop - 8,
              left: highlightedElement.offsetLeft - 8,
              width: highlightedElement.offsetWidth + 16,
              height: highlightedElement.offsetHeight + 16,
              zIndex: 9999,
              animation: 'pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite'
            }}
          />
        )}
      </div>

      {/* Tooltip du tour */}
      <div
        ref={tooltipRef}
        className="fixed z-[10000] pointer-events-auto"
        style={{
          top: tooltipPosition.top,
          left: tooltipPosition.left,
          transform: currentStep.position === 'center' ? 'translate(-50%, -50%)' : 
                    (currentStep.position === 'top' || currentStep.position === 'bottom') ? 'translateX(-50%)' : 
                    (currentStep.position === 'left' || currentStep.position === 'right') ? 'translateY(-50%)' : 
                    'none',
          maxWidth: currentStep.position === 'center' ? '500px' : '350px'
        }}
      >
        <div className="bg-white rounded-2xl shadow-2xl border border-gray-200 p-6 relative animate-in fade-in-50 slide-in-from-bottom-5 duration-300">
          {/* Indicateur de progression */}
          <div className="flex items-center justify-between mb-4">
            <div className="flex space-x-1">
              {steps.map((_, index) => (
                <div
                  key={index}
                  className={`w-2 h-2 rounded-full transition-all duration-300 ${
                    index <= currentStepIndex ? 'bg-blue-500' : 'bg-gray-300'
                  }`}
                />
              ))}
            </div>
            <button
              onClick={skipTour}
              className="p-1 hover:bg-gray-100 rounded-full transition-colors"
              aria-label="Fermer le tour"
            >
              <X className="w-4 h-4 text-gray-500" />
            </button>
          </div>

          {/* Contenu */}
          <div className="mb-6">
            <h3 className="text-xl font-bold text-gray-900 mb-3 flex items-center">
              {currentStep.title}
            </h3>
            <p className="text-gray-600 leading-relaxed">
              {currentStep.content}
            </p>
          </div>

          {/* Actions */}
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2 text-sm text-gray-500">
              <span>{currentStepIndex + 1}/{steps.length}</span>
            </div>
            
            <div className="flex items-center space-x-3">
              {!isFirstStep && (
                <button
                  onClick={prevStep}
                  className="flex items-center space-x-2 px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-all duration-200"
                >
                  <ArrowLeft className="w-4 h-4" />
                  <span>Précédent</span>
                </button>
              )}
              
              <button
                onClick={nextStep}
                className="flex items-center space-x-2 bg-gradient-to-r from-blue-500 to-indigo-500 text-white px-6 py-2 rounded-lg font-semibold hover:from-blue-600 hover:to-indigo-600 transition-all duration-200 shadow-lg hover:shadow-xl"
              >
                <span>{isLastStep ? 'Terminer' : 'Suivant'}</span>
                {isLastStep ? <CheckCircle className="w-4 h-4" /> : <ArrowRight className="w-4 h-4" />}
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default GuidedTour;